/**
 * Panel Labels — the package's i18n seam.
 *
 * The shell never imports i18next (or any i18n library): every user-visible
 * string resolves through one `resolveLabel(key, fallback?)` function the
 * host application supplies via `<PanelShell resolveLabel={t}>`. The default
 * resolver returns the English fallback (or the key), so the shell works
 * unlabeled out of the box.
 */

import React, { createContext, useContext, useMemo } from 'react';

/** Resolve a label key to display text. `fallback` is the English source
 * string; a resolver should return it when the key is unknown. */
export type LabelResolver = (key: string, fallback?: string) => string;

const identityResolver: LabelResolver = (key, fallback) => fallback ?? key;

const LabelContext = createContext<LabelResolver>(identityResolver);

export interface PanelLabelProviderProps {
    resolveLabel?: LabelResolver;
    children: React.ReactNode;
}

export const PanelLabelProvider: React.FC<PanelLabelProviderProps> = ({
    resolveLabel,
    children,
}) => {
    const value = useMemo(() => resolveLabel ?? identityResolver, [resolveLabel]);
    return <LabelContext.Provider value={value}>{children}</LabelContext.Provider>;
};

/** The active label resolver. Shadows the i18next `t(key, fallback?)` call
 * shape so shell call sites read the same as before extraction. */
export const usePanelLabel = (): LabelResolver => useContext(LabelContext);
